import CartModal from '@/components/CartModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { removeFromCart } from '@/apis/main';
import useApi from '@/hooks/useApi';
import useLocalStorage from '@/hooks/useLocalStorage';
import useToggle from '@/hooks/useToggle';
import CartDeleteModal from '@/components/CartDeleteModal';
import { PhotocardsProvider } from './_components/Provider';
import { GNB } from '@/components/GNB';
import { GroupFilter } from '@/components/GroupFilter';
import MemberFilter from '@/components/MemberFilter/MemberFilter';
import { CardsList } from '@/components/CardsList';
import { CartButton } from '@/components/CartButton';
import { FormattedMessage } from 'react-intl';
import { PickUpBanner } from '@/components/PickUpBanner/PickUpBanner';
import { useCart, useReset, useTimer } from '@/hooks';
import { useWebViewRouter } from '@/hooks/useWebViewRouter';
import { PhotocardListLayout } from './_components/PhotocardListLayout';
import { useSearchParams } from 'react-router-dom';
import { MainLogo } from '@/components/MainLogo';
import ReceivingMethodModal from '@/components/ReceivingMethodModal';
import PageViewEvent from '@/components/Event/PageViewEvent';
import MenuModal from '@/components/MenuModal';
import CompanyModal from '@/components/CompanyModal';
import BackModal from '@/components/BackModal';

const PhotocardListPage = () => {
  const [searchParams] = useSearchParams();
  const { direct } = useWebViewRouter();
  const { fetchApi } = useApi();
  const [currency] = useLocalStorage('currency', null);
  const { ref } = useTimer();
  const { cartData } = useCart({});
  const queryClient = useQueryClient();
  const { resetCart, goHome } = useReset();

  const companyModal = useToggle({ showing: false });
  const sortModal = useToggle({ showing: false });
  const cartModal = useToggle({ showing: false });
  const typeSectionModal = useToggle({ showing: false });
  const emptyCartModal = useToggle({ showing: false });
  const deleteModal = useToggle({ showing: false });
  const receivingModal = useToggle({ showing: false });
  const menuModal = useToggle({ showing: false });
  const backCheckModal = useToggle({ showing: false });
  const countryModal = useToggle({ showing: false });

  const category = searchParams.get('category') ?? null;
  const sort = searchParams.get('sort') ?? 'new';
  const group = searchParams.get('group') ?? '5';
  const member = searchParams.get('member') ?? '50';
  const q = searchParams.get('q') ?? '';

  const isShippingAbleState = (cartData?.offer_total ?? 0) >= 50000;

  const deleteCartMutation = useMutation({
    mutationFn: (offerList: number[]) => fetchApi((accessToken) => removeFromCart(offerList, accessToken)),
    mutationKey: ['cart'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      queryClient.invalidateQueries({ queryKey: ['card-list', group, member, category, sort, q] });
    }
  });

  const handleCartModalButtonClick = () => {
    if (cartData && cartData.cart_offers.length > 0) {
      if (isShippingAbleState) {
        receivingModal.open();
      } else {
        direct('/order');
      }
    } else {
      cartModal.close();
      emptyCartModal.open();
      setTimeout(() => {
        emptyCartModal.close();
      }, 800);
    }
  };

  return (
    <PageViewEvent eventName="PLP_view">
      <PhotocardListLayout>
        <PhotocardsProvider>
          <div
            ref={ref}
            onClick={() => {
              companyModal.isShowing && companyModal.close();
              sortModal.isShowing && sortModal.close();
              menuModal.isShowing && menuModal.close();
              countryModal.isShowing && countryModal.close();
            }}
          >
            <MainLogo />
            <GroupFilter />
            <div className="w-[calc(100%-260px)] h-screen ml-[260px] overflow-x-hidden flex flex-col items-end no-scrollbar">
              <GNB onClick={menuModal.open} countryModal={countryModal} />
              <MemberFilter sortModal={sortModal} typeSectionModal={typeSectionModal} />
              <CardsList onClick={() => typeSectionModal.isShowing && typeSectionModal.close()} />
              <div className="h-[102px] bg-white items-center flex justify-between py-[18px] gap-4 px-5 w-[calc(100%-260px)] fixed right-0 bottom-0">
                <PickUpBanner />
                <CartButton emptyTrigger={emptyCartModal} cartTrigger={cartModal} />
              </div>
              {emptyCartModal.isShowing && (
                <div className="w-[632px] h-[80px] rounded-[10px] flex justify-center items-center bg-[rgba(0,0,0,0.8)] fixed [top:calc(50%-40px)] [left:calc(50%-316px)] text-white text-[24px] leading-8 font-bold">
                  <FormattedMessage id="card-modal_empty" defaultMessage="Your cart is empty." />
                </div>
              )}
            </div>
          </div>

          {cartModal.isShowing && (
            <CartModal
              isSoloArtist={false}
              currency={currency}
              items={cartData?.cart_offers ?? []}
              onClose={cartModal.close}
              onItemDelete={(item) => deleteCartMutation.mutate([item.offer.id])}
              onAllItemDelete={deleteModal.open}
              onSubmit={handleCartModalButtonClick}
              offerTotal={cartData?.offer_total}
            />
          )}
          {deleteModal.isShowing && (
            <CartDeleteModal
              onClick={() => {
                deleteCartMutation.mutate([]);
                deleteModal.close();
              }}
              onClose={deleteModal.close}
            />
          )}

          {companyModal.isShowing && <CompanyModal onClose={companyModal.close} />}

          {backCheckModal.isShowing && (
            <BackModal
              onClose={backCheckModal.close}
              onClick={() => {
                resetCart();
                goHome();
              }}
            />
          )}

          {menuModal.isShowing && (
            <MenuModal
              cartData={cartData?.cart_offers}
              menuModal={menuModal}
              companyModalOpen={companyModal.open}
              backModalOpen={backCheckModal.open}
            />
          )}

          {receivingModal.isShowing && <ReceivingMethodModal onClose={receivingModal.close} />}
        </PhotocardsProvider>
      </PhotocardListLayout>
    </PageViewEvent>
  );
};

export default PhotocardListPage;
